import { AxiosError } from "axios";
import { useEffect, useState, useCallback } from "react";

/**
 * hook for fetching data from api
 *
 * @param fetcher function that returns a promise
 * @returns response, error, loading
 * @author Azher Uddin <https://github.com/AzherUddin617>
 * @date 2023-02-27 23:39:55
 */

const useFetch = <Type>(
  fetcher: () => Promise<Type>,
  conf: {
    skip?: boolean;
    deps?: any[];
  }
) => {
  const [response, setResponse] = useState<Type>();
  const [error, setError] = useState<AxiosError>();
  const [loading, setLoading] = useState<boolean>(false);

  const fetch = useCallback(
    async (f?: () => Promise<Type>) => {
      setLoading(true);

      try {
        const response = !!f ? await f() : await fetcher();

        setResponse(response);
        setLoading(false);
        setError(undefined);

        return Promise.resolve(response);
      } catch (error: any) {
        setError(error);
        setLoading(false);

        return Promise.reject(error);
      }
    },
    [fetcher, ...(conf.deps || [])]
  );

  useEffect(() => {
    if (!conf.skip) {
      // swallow the rejection here — the error is already captured in state;
      // without this every failed request becomes an unhandled promise
      // rejection (surfaced as an "Issue" by the Next.js dev overlay)
      fetch().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conf.skip, ...(conf.deps || [])]);

  return {
    response: response,
    error,
    loading,
    fetch,
  };
};

export default useFetch;
